import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Input, Spinner } from "reactstrap";
import Select from "react-select";

import { useCompanies } from "hooks/companies";
import SpecificEmpresaRequiredNotice from "../../../../components/SpecificEmpresaRequiredNotice";
import PageHero, { PageHeroRefreshButton } from "../../../../components/PageHero";
import ShellCard from "../../../../components/ShellCard";
import QuemEstaContratando from "../../../../components/gobid/QuemEstaContratando";
import SeloVerificado from "../../../../components/gobid/SeloVerificado";
import { LIMITE_CIDADES_ORIGEM, soDigitos, telefoneValido } from "../../../../components/gobid/perfilEmbarcador";
import { cidadesDoEstado } from "services/bidFreteApi";
import { listarEstados, previaPerfil } from "services/gobidPerfilApi";
import { makeReactSelectStyles, reactSelectPortalProps } from "utils/reactSelectStyles";
import { emailValido } from "../Leiloes/regras";
import useFormularioPerfil from "./useFormularioPerfil";
import "../Leiloes/styles.css";
import "../../Parametros/porAssunto/ParametrosPorAssunto.css";
import "./styles.css";

// GoBID › Operação › Perfil do embarcador (H2 PO1-3529, épico PO1-3527). O
// embarcador configura uma vez o que as transportadoras de FORA da carteira
// veem no convite do leilão e do BID ("Quem está contratando"). A prévia à
// direita é o mesmo bloco que vai no e-mail, montado pelo backend
// (POST /gobid/perfil/previa) com os valores ainda não salvos.

const DEBOUNCE_MS = 500;
const LIMITES = { apresentacao: 600, prazoPagamento: 80, exigencia: 200, contatoNome: 120, contatoEmail: 160 };
const selectStyles = makeReactSelectStyles();

const EXIGENCIAS = [
  { campo: "agendamento", label: "Agendamento", ajuda: "Ex.: coleta mediante agendamento com 24 h de antecedência." },
  { campo: "descarga", label: "Descarga", ajuda: "Ex.: descarga por conta do destinatário; paletizada." },
  { campo: "seguro", label: "Seguro", ajuda: "Ex.: seguro da carga por conta da transportadora (RCTR-C)." },
  { campo: "gerenciamentoRisco", label: "Gerenciamento de risco", ajuda: "Ex.: rastreador obrigatório acima de R$ 100 mil." },
];

const MOSTRAR = [
  { campo: "mostrarApresentacao", label: "Apresentação" },
  { campo: "mostrarSegmento", label: "Segmento" },
  { campo: "mostrarTipoCarga", label: "Tipo de carga" },
  { campo: "mostrarPrazoPagamento", label: "Prazo de pagamento" },
  { campo: "mostrarExigencias", label: "Como opera (agendamento, descarga, seguro, risco)" },
  { campo: "mostrarCidadesOrigem", label: "Cidades de origem" },
  { campo: "mostrarIndicadores", label: "Indicadores de volume por UF", ajuda: "Faixas de embarques/mês dos últimos 6 meses. Concorrentes seus podem estar entre os convidados. Só no leilão." },
];

function Switch({ ligado, onChange, disabled, rotulo }) {
  return (
    <button type="button" className={`pe-switch${ligado ? " is-on" : ""}`} onClick={() => onChange(!ligado)} disabled={disabled} aria-pressed={ligado} aria-label={rotulo}>
      {ligado ? "Mostrar" : "Oculto"}
    </button>
  );
}

function Campo({ label, children, contador }) {
  return (
    <div className="pe-campo">
      <label>{label}</label>
      {children}
      {contador ? <div className="pe-contador">{contador}</div> : null}
    </div>
  );
}

// Valida o que o backend também valida, para avisar antes do PUT.
export function validarFormulario(v) {
  const e = {};
  if (v.contatoEmail && !emailValido(v.contatoEmail)) e.contatoEmail = "E-mail inválido.";
  if (!telefoneValido(v.contatoTelefone)) e.contatoTelefone = "Telefone com 10 a 13 dígitos.";
  if (!telefoneValido(v.contatoWhatsapp)) e.contatoWhatsapp = "WhatsApp com 10 a 13 dígitos.";
  if (Array.isArray(v.cidadesOrigem) && v.cidadesOrigem.length > LIMITE_CIDADES_ORIGEM) e.cidadesOrigem = `No máximo ${LIMITE_CIDADES_ORIGEM} cidades.`;
  return e;
}

function CidadesOrigem({ valor, cidadesFiliais, cidadesSelecionadas, onChange, salvando }) {
  const [estados, setEstados] = useState([]);
  const [uf, setUf] = useState("");
  const [cidadesUf, setCidadesUf] = useState([]);
  const [carregandoCidades, setCarregandoCidades] = useState(false);
  // Nome/UF das cidades escolhidas: começa com o que o GET trouxe e ganha as
  // novas conforme o usuário escolhe (o id sozinho não dá para mostrar).
  const [catalogo, setCatalogo] = useState({});

  useEffect(() => {
    listarEstados()
      .then((lista) => setEstados(lista || []))
      .catch(() => setEstados([]));
  }, []);

  useEffect(() => {
    const mapa = {};
    (cidadesSelecionadas || []).forEach((c) => {
      mapa[c.id] = c;
    });
    (cidadesFiliais || []).forEach((c) => {
      if (!mapa[c.id]) mapa[c.id] = c;
    });
    setCatalogo((cur) => ({ ...cur, ...mapa }));
  }, [cidadesSelecionadas, cidadesFiliais]);

  useEffect(() => {
    if (!uf) {
      setCidadesUf([]);
      return undefined;
    }
    const estado = estados.find((x) => (x.uf || x.sigla) === uf);
    if (!estado || !estado.id) return undefined;
    let ativo = true;
    setCarregandoCidades(true);
    cidadesDoEstado(estado.id)
      .then((lista) => {
        if (ativo) setCidadesUf((lista || []).map((c) => ({ id: Number(c.id || c.Id), nome: c.nome || c.Nome, uf })));
      })
      .catch(() => {
        if (ativo) setCidadesUf([]);
      })
      .finally(() => {
        if (ativo) setCarregandoCidades(false);
      });
    return () => {
      ativo = false;
    };
  }, [uf, estados]);

  const usandoFiliais = valor === null || valor === undefined;
  const ids = usandoFiliais ? (cidadesFiliais || []).map((c) => c.id) : valor;
  const opcoes = useMemo(() => cidadesUf.map((c) => ({ value: c.id, label: c.nome, cidade: c })), [cidadesUf]);
  const selecionadasNaUf = opcoes.filter((o) => ids.indexOf(o.value) !== -1);

  const aplicar = (novos) => {
    const outrasUfs = ids.filter((id) => !opcoes.some((o) => o.value === id));
    const escolhidas = (novos || []).map((o) => o.value);
    const cat = {};
    (novos || []).forEach((o) => {
      cat[o.value] = o.cidade;
    });
    setCatalogo((cur) => ({ ...cur, ...cat }));
    onChange(outrasUfs.concat(escolhidas));
  };

  const remover = (id) => onChange(ids.filter((x) => x !== id));
  const rotulo = (id) => {
    const c = catalogo[id];
    return c ? `${c.nome}/${c.uf}` : `cidade ${id}`;
  };

  return (
    <>
      <div className="lf-muted" style={{ marginBottom: 8 }}>
        {usandoFiliais
          ? "Usando as cidades das suas filiais (padrão). Escolha cidades para montar a sua própria lista."
          : `${ids.length} de ${LIMITE_CIDADES_ORIGEM} cidades. `}
        {!usandoFiliais ? (
          <button type="button" className="lf-link" onClick={() => onChange(null)} disabled={salvando}>
            Usar as cidades das filiais
          </button>
        ) : null}
      </div>
      <div className="pe-cidades-seletor">
        <div className="pe-campo" style={{ marginBottom: 0 }}>
          <label htmlFor="pe-uf">UF</label>
          <Input id="pe-uf" type="select" value={uf} onChange={(e) => setUf(e.target.value)} disabled={salvando}>
            <option value="">—</option>
            {estados.map((x) => (
              <option key={x.id || x.uf} value={x.uf || x.sigla}>
                {x.uf || x.sigla} - {x.nome}
              </option>
            ))}
          </Input>
        </div>
        <div className="pe-campo" style={{ marginBottom: 0 }}>
          <label>Cidades da UF</label>
          <Select
            isMulti
            className="react-select info"
            classNamePrefix="react-select"
            options={opcoes}
            value={selecionadasNaUf}
            onChange={(v) => aplicar(v || [])}
            isDisabled={!uf || salvando}
            isLoading={carregandoCidades}
            styles={selectStyles}
            placeholder={uf ? "Escolha as cidades" : "Escolha a UF primeiro"}
            noOptionsMessage={() => "Sem opções"}
            {...reactSelectPortalProps()}
          />
        </div>
      </div>
      <div className="pe-cidades" data-testid="pe-cidades">
        {ids.map((id) => (
          <span key={id} className="pe-cidade">
            {rotulo(id)}
            {!usandoFiliais ? (
              <button type="button" onClick={() => remover(id)} aria-label={`Remover ${rotulo(id)}`} disabled={salvando}>
                ×
              </button>
            ) : null}
          </span>
        ))}
        {!ids.length ? <span className="lf-muted">Nenhuma cidade: o bloco "Cidades de origem" não aparece.</span> : null}
      </div>
    </>
  );
}

export default function PerfilEmbarcador() {
  const { empresa, hasEmpresaSelection } = useCompanies();
  const empresaId = empresa && empresa.value;
  const form = useFormularioPerfil(hasEmpresaSelection ? empresaId : null);
  const { valores, setValor, dados, carregando, erro, salvando, totalAlterados, descartar, salvar, recarregar } = form;
  const [fluxo, setFluxo] = useState("leilao");
  const [previa, setPrevia] = useState(null);
  const [carregandoPrevia, setCarregandoPrevia] = useState(false);
  const [erroPrevia, setErroPrevia] = useState(null);
  const timerRef = useRef(null);
  const seqRef = useRef(0);

  const erros = useMemo(() => validarFormulario(valores), [valores]);
  const temErro = Object.keys(erros).length > 0;

  // Prévia com debounce: a cada alteração (ou troca de fluxo), 500 ms depois.
  const chave = JSON.stringify(valores);
  useEffect(() => {
    if (!empresaId || !hasEmpresaSelection || !dados) return undefined;
    if (timerRef.current) clearTimeout(timerRef.current);
    const seq = ++seqRef.current;
    timerRef.current = setTimeout(async () => {
      setCarregandoPrevia(true);
      try {
        const r = await previaPerfil(empresaId, fluxo, JSON.parse(chave));
        if (seq === seqRef.current) {
          setPrevia(r);
          setErroPrevia(null);
        }
      } catch (e) {
        if (seq === seqRef.current) setErroPrevia((e.response && e.response.data && e.response.data.error) || "Não foi possível montar a prévia.");
      } finally {
        if (seq === seqRef.current) setCarregandoPrevia(false);
      }
    }, DEBOUNCE_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [empresaId, hasEmpresaSelection, dados, fluxo, chave]);

  if (!hasEmpresaSelection) {
    return (
      <div className="content ff-page">
        <SpecificEmpresaRequiredNotice title="Selecione um embarcador no cabeçalho" message="Esta tela configura o perfil do embarcador selecionado no topo, que as transportadoras de fora da carteira veem no convite." />
      </div>
    );
  }

  const texto = (campo, max) => (
    <Input
      type={max > 200 ? "textarea" : "text"}
      rows={max > 200 ? 4 : undefined}
      maxLength={max}
      value={valores[campo] || ""}
      onChange={(e) => setValor(campo, e.target.value)}
      disabled={salvando || carregando}
      invalid={Boolean(erros[campo])}
      aria-label={campo}
    />
  );
  const contador = (campo, max) => `${String(valores[campo] || "").length}/${max}`;
  const opcoes = (dados && dados.opcoes) || {};
  const cadastro = (dados && dados.cadastro) || {};
  const verificado = Boolean(dados && dados.perfil && dados.perfil.verificado);

  return (
    <div className="content ff-page">
      <PageHero
        eyebrow="GoBID · Operação"
        title="Perfil do embarcador"
        description="O que as transportadoras de fora da sua carteira veem sobre você no convite do leilão e do BID. Quem já é da carteira não recebe este bloco."
        actions={<PageHeroRefreshButton onClick={recarregar} loading={carregando} />}
      >
        <div className="lf-muted" style={{ marginTop: 6 }}>
          {cadastro.fantasia || cadastro.razaoSocial || ""}
          {cadastro.cnpj ? ` · CNPJ ${cadastro.cnpj}` : ""}
          <SeloVerificado verificado={verificado} />
        </div>
      </PageHero>

      {erro ? <div className="lf-aviso lf-aviso-danger">{erro}</div> : null}
      {carregando && !dados ? (
        <div className="lf-empty-state">
          <Spinner size="sm" /> Carregando o perfil...
        </div>
      ) : dados ? (
        <div className="pe-layout">
          <div>
            <ShellCard title="Apresentação" description="Quem é a sua empresa, em poucas linhas. Vai no topo do bloco.">
              <Campo label="Apresentação" contador={contador("apresentacao", LIMITES.apresentacao)}>
                {texto("apresentacao", LIMITES.apresentacao)}
              </Campo>
            </ShellCard>

            <ShellCard title="Segmento e tipo de carga" description="Ajuda a transportadora a saber se a sua carga cabe na frota dela.">
              <div className="bf-grid-2" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: "10px 14px" }}>
                <Campo label="Segmento">
                  <Input type="select" value={valores.segmento || ""} onChange={(e) => setValor("segmento", e.target.value)} disabled={salvando} aria-label="segmento">
                    <option value="">—</option>
                    {(opcoes.segmentos || []).map((o) => (
                      <option key={o.codigo} value={o.codigo}>
                        {o.rotulo}
                      </option>
                    ))}
                  </Input>
                </Campo>
                <Campo label="Tipo de carga">
                  <Input type="select" value={valores.tipoCarga || ""} onChange={(e) => setValor("tipoCarga", e.target.value)} disabled={salvando} aria-label="tipoCarga">
                    <option value="">—</option>
                    {(opcoes.tiposCarga || []).map((o) => (
                      <option key={o.codigo} value={o.codigo}>
                        {o.rotulo}
                      </option>
                    ))}
                  </Input>
                </Campo>
              </div>
            </ShellCard>

            <ShellCard title="Como opera" description="Prazo de pagamento e exigências. Cada campo é opcional; vazio não aparece.">
              <Campo label="Prazo de pagamento" contador={contador("prazoPagamento", LIMITES.prazoPagamento)}>
                {texto("prazoPagamento", LIMITES.prazoPagamento)}
              </Campo>
              {EXIGENCIAS.map((x) => (
                <Campo key={x.campo} label={x.label} contador={contador(x.campo, LIMITES.exigencia)}>
                  {texto(x.campo, LIMITES.exigencia)}
                  <div className="lf-muted">{x.ajuda}</div>
                </Campo>
              ))}
            </ShellCard>

            <ShellCard title="Cidades de origem" description="De onde a carga sai. Por padrão, as cidades das suas filiais.">
              <CidadesOrigem valor={valores.cidadesOrigem} cidadesFiliais={dados.cidadesFiliais} cidadesSelecionadas={dados.cidadesSelecionadas} onChange={(v) => setValor("cidadesOrigem", v)} salvando={salvando} />
              {erros.cidadesOrigem ? <div className="lf-erro-campo">{erros.cidadesOrigem}</div> : null}
            </ShellCard>

            <ShellCard title="Contato padrão" description="Com quem a transportadora fala. Sem contato aqui, vai o nome e o e-mail de quem criou o leilão ou o BID.">
              {[
                { campo: "contatoNome", mostrar: "mostrarContatoNome", label: "Nome", max: LIMITES.contatoNome },
                { campo: "contatoEmail", mostrar: "mostrarContatoEmail", label: "E-mail", max: LIMITES.contatoEmail },
                { campo: "contatoTelefone", mostrar: "mostrarContatoTelefone", label: "Telefone", max: 20, digitos: true },
                { campo: "contatoWhatsapp", mostrar: "mostrarContatoWhatsapp", label: "WhatsApp", max: 20, digitos: true },
              ].map((c) => (
                <div key={c.campo} className="pe-contato-linha">
                  <div className="pe-campo" style={{ marginBottom: 0 }}>
                    <label>{c.label}</label>
                    <Input
                      type="text"
                      inputMode={c.digitos ? "tel" : undefined}
                      maxLength={c.max}
                      value={valores[c.campo] || ""}
                      onChange={(e) => setValor(c.campo, c.digitos ? soDigitos(e.target.value) : e.target.value)}
                      disabled={salvando}
                      invalid={Boolean(erros[c.campo])}
                      aria-label={c.campo}
                      placeholder={c.digitos ? "Só números, com DDD" : undefined}
                    />
                    {erros[c.campo] ? <div className="lf-erro-campo">{erros[c.campo]}</div> : null}
                  </div>
                  <Switch ligado={Boolean(valores[c.mostrar])} onChange={(v) => setValor(c.mostrar, v)} disabled={salvando} rotulo={`Mostrar ${c.label} ao convidado`} />
                </div>
              ))}
              <div className="lf-aviso lf-aviso-warning" style={{ marginTop: 6 }}>
                Telefone e WhatsApp vêm desligados: ligando, quem está fora da carteira vê o número, inclusive concorrentes do convidado.
              </div>
            </ShellCard>

            <ShellCard title="O que mostrar" description="Um interruptor por bloco. O que estiver oculto não aparece em nenhum canal.">
              {MOSTRAR.map((m) => (
                <div key={m.campo} className="pe-mostrar">
                  <div>
                    <div className="pe-mostrar__texto">{m.label}</div>
                    {m.ajuda ? <div className="pe-mostrar__ajuda">{m.ajuda}</div> : null}
                  </div>
                  <Switch ligado={Boolean(valores[m.campo])} onChange={(v) => setValor(m.campo, v)} disabled={salvando} rotulo={m.label} />
                </div>
              ))}
            </ShellCard>

            <ShellCard title="Selo" description="Só a Everlog liga o selo, depois de conferir o cadastro.">
              {verificado ? (
                <div className="lf-text-success" data-testid="pe-selo">
                  Verificado pela Everlog{dados.perfil && dados.perfil.verificadoEm ? ` em ${new Date(dados.perfil.verificadoEm).toLocaleDateString("pt-BR")}` : ""}.
                </div>
              ) : (
                <div className="lf-muted" data-testid="pe-selo">
                  A Everlog ainda não verificou este cadastro.
                </div>
              )}
            </ShellCard>
          </div>

          <div className="pe-previa">
            <ShellCard
              title="Prévia"
              description="Assim o convidado de fora da carteira vê o bloco."
              action={
                <div className="lf-presets">
                  {[
                    { chave: "leilao", rotulo: "No leilão" },
                    { chave: "bid", rotulo: "No BID" },
                  ].map((f) => (
                    <button key={f.chave} type="button" className={`lf-preset${fluxo === f.chave ? " is-active" : ""}`} onClick={() => setFluxo(f.chave)}>
                      {f.rotulo}
                    </button>
                  ))}
                </div>
              }
            >
              <div className="pe-previa__topo">
                <span className="lf-muted">{carregandoPrevia ? "Atualizando a prévia..." : totalAlterados ? "Prévia com as alterações ainda não salvas." : "Prévia do perfil salvo."}</span>
              </div>
              {erroPrevia ? <div className="lf-aviso lf-aviso-danger">{erroPrevia}</div> : null}
              {previa ? <QuemEstaContratando perfil={previa} style={{ marginBottom: 0 }} /> : !erroPrevia ? <div className="lf-empty-state">{carregandoPrevia ? <Spinner size="sm" /> : "Sem prévia."}</div> : null}
            </ShellCard>
          </div>
        </div>
      ) : null}

      {totalAlterados ? (
        <div className="ppa-savebar" role="region" aria-label="Alterações do perfil">
          <span className="ppa-savebar__texto">
            {totalAlterados} campo{totalAlterados === 1 ? "" : "s"} alterado{totalAlterados === 1 ? "" : "s"}
            {temErro ? " · corrija os campos em vermelho" : ""}
          </span>
          <Button color="secondary" outline size="sm" onClick={descartar} disabled={salvando}>
            Descartar
          </Button>
          <Button color="primary" size="sm" onClick={salvar} disabled={salvando || temErro}>
            {salvando ? "Salvando..." : "Salvar alterações"}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
